import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isApiError } from '@/api/errors';
import type { ApiKeyView, OperatingMode } from '@/api/types';
import { PermissionChecklist } from '@/components/PermissionChecklist';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FieldGroup, Field } from '@/components/ui/field';
import { permissionsFieldError } from '@/lib/keyPermissions';

export function EditKeyPermissionsDialog({
  apiKey,
  savePermissions,
  invalidateKey,
  availablePermissions,
  operatingMode,
  onDone,
}: {
  apiKey: ApiKeyView;
  savePermissions: (keyId: string, permissions: string[]) => Promise<ApiKeyView>;
  invalidateKey: readonly unknown[];
  availablePermissions: readonly string[];
  /** Set for a partner key; leaves the checklist ungrouped when absent (org keys). */
  operatingMode?: OperatingMode | undefined;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [permissions, setPermissions] = useState<string[]>(apiKey.permissions);
  const [touched, setTouched] = useState(false);

  const save = useMutation({
    mutationFn: () => savePermissions(apiKey.id, permissions),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invalidateKey });
      onDone();
    },
  });

  const permissionsError = permissionsFieldError(save.error);
  const emptyError =
    touched && permissions.length === 0 ? 'Select at least one permission.' : undefined;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit permissions</DialogTitle>
        <DialogDescription>
          {apiKey.name} · {apiKey.key_prefix}. Saving replaces the key’s permissions.
        </DialogDescription>
      </DialogHeader>

      {save.isError && !permissionsError && (
        <Alert variant="destructive">
          <AlertDescription>
            {isApiError(save.error) ? save.error.message : 'Could not save the permissions.'}
          </AlertDescription>
        </Alert>
      )}

      <form
        id="edit-key-permissions"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (permissions.length > 0) save.mutate();
        }}
      >
        <FieldGroup>
          <Field data-invalid={Boolean(permissionsError ?? emptyError)}>
            <PermissionChecklist
              value={permissions}
              onChange={setPermissions}
              available={availablePermissions}
              operatingMode={operatingMode}
              error={permissionsError ?? emptyError}
            />
          </Field>
        </FieldGroup>
      </form>

      <DialogFooter>
        <Button type="submit" form="edit-key-permissions" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save permissions'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
